// Ringtone de "chamando" via Web Audio API.
//
// Nenhum asset MP3 necessário: geramos o som em tempo real com dois osciladores
// (campainha telefônica americana/BR: 440 Hz + 480 Hz batendo). O padrão é
// "bipe bipe (1s), silêncio (3s) — loop" — igual um telefone convencional.
//
// Retém um único `AudioContext` por janela (autoplay policy do navegador só
// permite startar após uma interação do usuário; a maioria dos cliques em
// botões do painel já cumpre esta regra).

let ctx = null;
let ringLoopTimer = null;
let nodesAtuais = null;
let volumeAtual = 0.25;

// OVERRIDE independente do volume de base.
// ganho FINAL tocado = volumeAtual (0.25 padrão) * notificationVolume01.
// Ex: slider 0% → 0.25 * 0 = 0 (SILÊNCIO ABSOLUTO no ringtone também).
// Ex: slider 50% → 0.25 * 0.5 = 0.125
//
// DEFAULT INICIAL SEGURO = 0 (MUDO TOTAL até sabermos o valor real).
// Evita que o ringtone toque nos primeiros 200ms antes do /api/auth/me
// retornar — igual ao chatInternoSom.
let notificationVolume01 = 0;

// Leitura AUTÔNOMA de localStorage na inicialização do módulo (igual ao
// chatInternoSom). Mesmo se useAuth ainda não montou, este módulo já sabe
// qual era a preferência salva do usuário neste navegador.
(function aplicarLocalStorageNoStartup() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    const prefixo = "PERABIT_NOTIF_VOLUME_v1_";
    const todas = Object.keys(window.localStorage).filter(k => k && k.startsWith(prefixo));
    if (todas.length === 0) return;
    const chave = todas[todas.length - 1];
    const raw = window.localStorage.getItem(chave);
    if (!raw) return;
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    const clamp = Math.max(0, Math.min(100, n));
    notificationVolume01 = clamp / 100;
  } catch (_e) { /* noop */ }
})();

const pegarCtx = () => {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
  }
  if (ctx.state === "suspended") {
    ctx.resume().catch(() => undefined);
  }
  return ctx;
};

// Uma "rajada" de som: dois osciladores em batimento, ~425ms de duração.
const tocarRajada = () => {
  const ganhoFinal = Math.max(0, Math.min(1, Number(volumeAtual || 0) * Number(notificationVolume01 || 0)));
  // THRESHOLD PARANOICO 1% (0.01): abaixo disso não criamos nós de
  // áudio nenhum. Evita bugs do Web Audio em que 0.0 exato ou muito
  // baixo é arredondado para o volume padrão (1) por baixo dos panos.
  if (ganhoFinal <= 0.01) return;

  const c = pegarCtx();
  if (!c) return;

  const o1 = c.createOscillator();
  const o2 = c.createOscillator();
  const g = c.createGain();

  o1.type = "sine";
  o2.type = "sine";
  o1.frequency.value = 440;
  o2.frequency.value = 480;

  const agora = c.currentTime;
  g.gain.setValueAtTime(0.0001, agora);
  g.gain.exponentialRampToValueAtTime(ganhoFinal, agora + 0.02);
  g.gain.setValueAtTime(ganhoFinal, agora + 0.40);
  g.gain.exponentialRampToValueAtTime(0.0001, agora + 0.43);

  o1.connect(g);
  o2.connect(g);
  g.connect(c.destination);

  o1.start(agora);
  o2.start(agora);
  o1.stop(agora + 0.45);
  o2.stop(agora + 0.45);

  nodesAtuais = { o1, o2, g };

  setTimeout(() => {
    try { o1.disconnect(); } catch (_e) {}
    try { o2.disconnect(); } catch (_e) {}
    try { g.disconnect(); } catch (_e) {}
  }, 600);
};

const tocarDoisBipes = () => {
  tocarRajada();
  setTimeout(tocarRajada, 520);
};

export const startRingtone = () => {
  // EARLY RETURN no mudo: nem cria o loop de repetição, só retorna.
  const ganhoFinal = Math.max(0, Math.min(1, Number(volumeAtual || 0) * Number(notificationVolume01 || 0)));
  // THRESHOLD PARANOICO 1% (abaixo disso nem consideramos ringtone):
  if (ganhoFinal <= 0.01) return;

  stopRingtone();
  const c = pegarCtx();
  if (!c) return;
  tocarDoisBipes();
  ringLoopTimer = setInterval(tocarDoisBipes, 4200);
};

export const stopRingtone = () => {
  if (ringLoopTimer) {
    clearInterval(ringLoopTimer);
    ringLoopTimer = null;
  }
  if (nodesAtuais) {
    const c = pegarCtx();
    try { nodesAtuais.o1?.stop?.(); } catch (_e) {}
    try { nodesAtuais.o2?.stop?.(); } catch (_e) {}
    nodesAtuais = null;
  }
};

export const setRingtoneVolume = (v) => {
  volumeAtual = Math.max(0, Math.min(1, Number(v) || 0));
};

// Intensidade geral da preferência do usuário 0..1.
// Chamado no useAuth quando user.notificationVolume muda.
export const setRingtoneNotifVolume01 = (fator01, { userId = null } = {}) => {
  notificationVolume01 = Math.max(0, Math.min(1, Number(fator01) || 0));
  // Grava localStorage para o próximo F5 iniciar instantaneamente com o
  // valor certo (mesmo padrão chatInternoSom).
  try {
    if (!userId || typeof window === "undefined" || !window.localStorage) return;
    const chave = `PERABIT_NOTIF_VOLUME_v1_${Number(userId) || 0}`;
    const comoPercentual = Math.max(0, Math.min(100, Math.round(notificationVolume01 * 100)));
    window.localStorage.setItem(chave, String(comoPercentual));
  } catch (_e) { /* noop */ }
};
